/**
 * Unified logging utility for production-safe structured logging
 * Consolidates optimizedStructuredLogging functionality
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';
export type LogContext =
  | 'experiment'
  | 'hypothesis'
  | 'auth'
  | 'dashboard'
  | 'api'
  | 'performance'
  | 'system'
  | 'ui';

export interface LogEntry {
  level: LogLevel;
  context: LogContext;
  message: string;
  timestamp: Date;
  metadata?: Record<string, unknown>;
}

/**
 * Production-safe structured logger
 * Only logs to console in development or for errors
 */
class Logger {
  private logHistory: LogEntry[] = [];
  private readonly maxHistorySize = 100;

  log(entry: LogEntry): void {
    this.logHistory.push(entry);
    if (this.logHistory.length > this.maxHistorySize) {
      this.logHistory.shift();
    }

    if (process.env.NODE_ENV === 'development' || entry.level === 'error') {
      const logMethod = console[entry.level] || console.log;
      const prefix = `[${entry.context.toUpperCase()}]`;

      if (entry.metadata) {
        logMethod(prefix, entry.message, entry.metadata);
      } else {
        logMethod(prefix, entry.message);
      }
    }
  }

  debug(
    context: LogContext,
    message: string,
    metadata?: Record<string, unknown>
  ): void {
    this.log({
      level: 'debug',
      context,
      message,
      timestamp: new Date(),
      metadata,
    });
  }

  info(
    context: LogContext,
    message: string,
    metadata?: Record<string, unknown>
  ): void {
    this.log({
      level: 'info',
      context,
      message,
      timestamp: new Date(),
      metadata,
    });
  }

  warn(
    context: LogContext,
    message: string,
    metadata?: Record<string, unknown>
  ): void {
    this.log({
      level: 'warn',
      context,
      message,
      timestamp: new Date(),
      metadata,
    });
  }

  error(
    context: LogContext,
    message: string,
    metadata?: Record<string, unknown>
  ): void {
    this.log({
      level: 'error',
      context,
      message,
      timestamp: new Date(),
      metadata,
    });
  }

  getRecentLogs(count = 50): LogEntry[] {
    return this.logHistory.slice(0, count);
  }

  getLogs(filter: Partial<Pick<LogEntry, 'level' | 'context'>>): LogEntry[] {
    return this.logHistory.filter((entry) => {
      if (filter.level && entry.level !== filter.level) return false;
      if (filter.context && entry.context !== filter.context) return false;
      return true;
    });
  }

  clearHistory(): void {
    this.logHistory = [];
  }
}

export const logger = new Logger();

// Context-specific loggers
export const experimentLogger = {
  created: (experimentId: string, metadata?: Record<string, unknown>) =>
    logger.info('experiment', `Experiment created: ${experimentId}`, metadata),
  updated: (experimentId: string, metadata?: Record<string, unknown>) =>
    logger.info('experiment', `Experiment updated: ${experimentId}`, metadata),
  deleted: (experimentId: string) =>
    logger.info('experiment', `Experiment deleted: ${experimentId}`),
  statusChanged: (experimentId: string, status: string) =>
    logger.info('experiment', `Status changed: ${status}`, { experimentId }),
};

export const hypothesisLogger = {
  created: (hypothesisId: string, metadata?: Record<string, unknown>) =>
    logger.info('hypothesis', `Hypothesis created: ${hypothesisId}`, metadata),
  updated: (hypothesisId: string, metadata?: Record<string, unknown>) =>
    logger.info('hypothesis', `Hypothesis updated: ${hypothesisId}`, metadata),
  deleted: (hypothesisId: string) =>
    logger.info('hypothesis', `Hypothesis deleted: ${hypothesisId}`),
  linked: (hypothesisId: string, experimentId: string) =>
    logger.info('hypothesis', 'Hypothesis linked to experiment', {
      hypothesisId,
      experimentId,
    }),
};

export const performanceLogger = {
  renderTime: (component: string, duration: number) =>
    logger.debug('performance', `${component} render: ${duration}ms`, {
      component,
      duration,
    }),
  apiCall: (endpoint: string, duration: number) =>
    logger.debug('performance', `API call: ${endpoint} (${duration}ms)`, {
      endpoint,
      duration,
    }),
  slowOperation: (operation: string, duration: number, threshold: number) =>
    logger.warn('performance', `Slow operation: ${operation} (${duration}ms)`, {
      operation,
      duration,
      threshold,
    }),
};

export const apiLogger = {
  request: (
    endpoint: string,
    method: string,
    metadata?: Record<string, unknown>
  ) => logger.debug('api', `${method} ${endpoint}`, metadata),
  success: (endpoint: string, metadata?: Record<string, unknown>) =>
    logger.info('api', `Success: ${endpoint}`, metadata),
  error: (endpoint: string, error: Error, metadata?: Record<string, unknown>) =>
    logger.error('api', `Error: ${endpoint} - ${error.message}`, {
      ...metadata,
      error,
    }),
};
